import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Import hero slide images - standard
import heroSlideStudio from "@/assets/hero-slide-studio.png";
import heroSlideUrban from "@/assets/hero-slide-urban.png";
import heroSlideArchitectural from "@/assets/hero-slide-architectural.png";

// Import responsive srcsets for optimized loading
import heroSlideStudioSrcset from "@/assets/hero-slide-studio.png?responsive";
import heroSlideUrbanSrcset from "@/assets/hero-slide-urban.png?responsive";
import heroSlideArchitecturalSrcset from "@/assets/hero-slide-architectural.png?responsive";

interface ResponsiveSrcset {
  srcset: string;
  src: string;
}

interface Slide {
  image: string;
  srcset: ResponsiveSrcset;
  alt: string;
  headline: string;
  headlineAccent?: string;
  subtext: string;
  buttonText: string;
  buttonLink: string;
}

const slides: Slide[] = [
  {
    image: heroSlideStudio,
    srcset: heroSlideStudioSrcset as unknown as ResponsiveSrcset,
    alt: "2025 Audi Q7 Black Optic luxury SUV front view - Rick's Black Car Service San Antonio fleet",
    headline: "Effortless Luxury.",
    headlineAccent: "Unforgettable Journeys.",
    subtext: "Experience San Antonio's premier chauffeur service in our 2025 Audi Q7 Black Optic.",
    buttonText: "BOOK YOUR RIDE",
    buttonLink: "/book",
  },
  {
    image: heroSlideUrban,
    srcset: heroSlideUrbanSrcset as unknown as ResponsiveSrcset,
    alt: "Executive black car chauffeur service driving at night in San Antonio for SAT airport transfer",
    headline: "Transparent Pricing.",
    headlineAccent: "No Hidden Fees.",
    subtext: "Flat-Rate Airport Transfers & Hourly Charters.",
    buttonText: "VIEW RATES",
    buttonLink: "/services",
  },
  {
    image: heroSlideArchitectural,
    srcset: heroSlideArchitecturalSrcset as unknown as ResponsiveSrcset,
    alt: "Professional VIP corporate transportation arriving at luxury hotel destination in San Antonio Texas",
    headline: "Professional. Punctual.",
    headlineAccent: "Exclusive.",
    subtext: "Serving SAT Airport, Hill Country, and Corporate Events.",
    buttonText: "GET A QUOTE",
    buttonLink: "/request-quote",
  },
];

const LuxuryHeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  // Auto-advance slides every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  return (
    <section className="relative h-screen min-h-[100dvh] w-full overflow-hidden bg-[#0D0D0D]">
      {/* Persistent Top-Left Branding - Always Visible */}
      <div className="absolute top-24 md:top-28 left-4 sm:left-6 md:left-12 z-30 max-w-xs sm:max-w-md md:max-w-lg">
        <h1 
          className="font-playfair text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight"
          style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6), 0 4px 16px rgba(0,0,0,0.4)' }}
        >
          <span className="text-[#C5A059]" style={{ textShadow: '0 0 15px rgba(197,160,89,0.8), 0 0 30px rgba(197,160,89,0.6), 0 0 50px rgba(212,175,55,0.4)' }}>Black Car Service</span> & Luxury Airport Transportation
          <span 
            className="block text-[#C5A059]/90 font-sans text-base sm:text-lg md:text-xl lg:text-2xl font-light tracking-wide mt-2"
            style={{ textShadow: '0 0 12px rgba(197,160,89,0.6), 0 0 25px rgba(197,160,89,0.4)' }}
          >
            in San Antonio
          </span>
        </h1>
      </div>

      {/* JBSA Base Access Badge - Bottom Left with Pulsing Gold Glow */}
      <Link
        to="/jbsa-military-transportation"
        className="absolute bottom-24 md:bottom-12 left-4 md:left-12 z-30 group"
      >
        <div className="relative">
          {/* Animated Gold Glow Effect - Subtle */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-[#C5A059] via-[#D4AF37] to-[#C5A059] rounded-sm opacity-40 blur-sm animate-[pulse_3s_ease-in-out_infinite]" />
          <div className="absolute -inset-1 bg-gradient-to-r from-[#D4AF37] via-[#E2BE7A] to-[#D4AF37] rounded-sm opacity-20 blur-md animate-[pulse_3s_ease-in-out_infinite_0.5s]" />
          
          {/* Badge Content */}
          <div className="relative flex items-center gap-2 md:gap-3 bg-black/85 backdrop-blur-sm border border-[#C5A059]/60 rounded-sm px-3 md:px-4 py-2 md:py-3 transition-all duration-300 hover:bg-black/90 hover:border-[#D4AF37]">
            <div className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 bg-[#C5A059]/30 rounded-full flex-shrink-0">
              <span className="text-sm md:text-lg">🛡️</span>
            </div>
            <div className="min-w-0">
              <p className="text-[#D4AF37] font-sans text-[10px] md:text-xs uppercase tracking-wider md:tracking-widest font-semibold">
                JBSA Base Access
              </p>
              <p className="text-white/70 font-sans text-[10px] md:text-xs tracking-wide group-hover:text-white transition-colors truncate">
                Lackland • Ft. Sam • Randolph
              </p>
            </div>
          </div>
        </div>
      </Link>

      {/* Background Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          {/* Ken Burns Zoom + Pan Container - Alternating directions */}
          <motion.div
            key={`ken-burns-${currentSlide}`}
            initial={{ 
              scale: currentSlide % 2 === 0 ? 1 : 1.12,
              x: currentSlide % 3 === 0 ? "-2%" : currentSlide % 3 === 1 ? "2%" : "0%",
            }}
            animate={{ 
              scale: currentSlide % 2 === 0 ? 1.12 : 1,
              x: currentSlide % 3 === 0 ? "2%" : currentSlide % 3 === 1 ? "-2%" : "0%",
            }}
            transition={{ 
              duration: 5.5, 
              ease: "easeOut",
            }}
            className="absolute inset-0"
          >
            {/* Background Image with SEO Alt Text, Lazy Loading & Responsive srcset */}
            <img
              src={slides[currentSlide].srcset?.src || slides[currentSlide].image}
              srcSet={slides[currentSlide].srcset?.srcset}
              sizes="100vw"
              alt={slides[currentSlide].alt}
              loading={currentSlide === 0 ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={currentSlide === 0 ? "high" : "auto"}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </motion.div>

          {/* Dark Gradient Overlay - Cinematic */}
          <div 
            className="absolute inset-0 z-[2]"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 100%)'
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 flex h-full items-center justify-center px-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Headline */}
            <h2 
              className="font-playfair text-white mb-4"
              style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}
            >
              <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-wide leading-tight">
                {slides[currentSlide].headline}
              </span>
              {slides[currentSlide].headlineAccent && (
                <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-[#C5A059] mt-2">
                  {slides[currentSlide].headlineAccent}
                </span>
              )}
            </h2>

            {/* Subtext - Montserrat style */}
            <p 
              className="font-sans text-base sm:text-lg md:text-xl text-[#f0f0f0] font-light tracking-[1px] mt-6 max-w-2xl mx-auto"
            >
              {slides[currentSlide].subtext}
            </p>

            {/* CTA Button - Gold with White Hover */}
            <Link
              to={slides[currentSlide].buttonLink}
              className="inline-block mt-8 px-9 py-4 bg-[#C5A059] hover:bg-white text-black hover:text-[#C5A059] font-semibold text-sm tracking-widest uppercase transition-all duration-300 border-2 border-transparent hover:border-[#C5A059]"
            >
              {slides[currentSlide].buttonText}
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={() => {
          prevSlide();
          setIsAutoPlaying(false);
          setTimeout(() => setIsAutoPlaying(true), 10000);
        }}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 p-3 text-white/60 hover:text-white transition-colors duration-300 group"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-8 h-8 md:w-10 md:h-10 group-hover:scale-110 transition-transform" />
      </button>

      <button
        onClick={() => {
          nextSlide();
          setIsAutoPlaying(false);
          setTimeout(() => setIsAutoPlaying(true), 10000);
        }}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 p-3 text-white/60 hover:text-white transition-colors duration-300 group"
        aria-label="Next slide"
      >
        <ChevronRight className="w-8 h-8 md:w-10 md:h-10 group-hover:scale-110 transition-transform" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-12 md:w-16 h-1 rounded-full transition-all duration-500 ${
              index === currentSlide
                ? "bg-[#C5A059]"
                : "bg-white/30 hover:bg-white/50"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
          className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            className="w-1.5 h-3 bg-[#C5A059] rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default LuxuryHeroSlider;
